import { useLocation, useOutletContext } from 'react-router-dom';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useRecoilValue } from 'recoil';

import { iaRa, pm } from 'lib/helpers/utility';
import { PRIMARY_NEW_BUTTON } from 'lib/hooks/useNewButtonStyle';
import { useTableContext } from 'lib/context/TableContext/TableContext';
import { formatMedicationData } from 'lib/helpers/formatMedicationData';
import { useMedication } from 'lib/hooks/queries/medication/useMedication';

import { permissions } from 'components/state';
import Allowed from 'components/shared/Permissions/Allowed';
import { currentPractice } from 'components/practice/practiceState';
import AgTablePage from 'components/shared/AgTablePage/AgTablePage';
import { DEFAULT_FILTERS } from 'components/Patient/medications/configs';
import medicationsCols from 'components/practice/charts/Overview/widgets/lib/medicationsCols';
import { SERVER_SORTED_COL_DEF } from 'components/practice/charts/HealthRecords/lib/serverSortedTable';
import UpsertProviderMedication from 'components/Forms/Custom/ProviderMedications/components/UpsertMedication';
import MedicationTableViewSwitch from 'components/Forms/Custom/ProviderMedications/components/MedicationTableViewSwitch/MedicationTableViewSwitch';
import {
  MEDICATION_PRODUCT_NDC_GROUPING_PROPS,
  useMedicationProductNdcGrouping
} from 'components/Forms/Custom/ProviderMedications/hooks/useMedicationProductNdcGrouping';

const MedicationNdcGrouping = ({ rawQueryData }) => {
  const medications = rawQueryData?.medicationHistory?.response?.medications ?? [];
  useMedicationProductNdcGrouping({ medications });
  return null;
};

// The route nests the total under medicationHistory.response; AgTablePage reads data.count to
// decide whether there is more than one page.
const usePagedMedication = (args) => {
  const result = useMedication({
    ...args,
    dependencies: [String(args?.params?.patientId), ...iaRa(args?.dependencies)]
  });

  const data = useMemo(
    () =>
      result.data
        ? { ...result.data, count: result.data?.medicationHistory?.response?.count }
        : result.data,
    [result.data]
  );

  return { ...result, data };
};

// Resets to page 1 on view change.
const ViewSwitch = ({ activeView, onChange, variant, sizedByContainer }) => {
  const { setPage } = useTableContext();

  return (
    <MedicationTableViewSwitch
      activeView={activeView}
      allLabel="All"
      variant={variant}
      sizedByContainer={sizedByContainer}
      onChange={(next) => {
        setPage(1);
        onChange(next);
      }}
    />
  );
};

const PatientHistoryMedications = () => {
  const userPermissions = useRecoilValue(permissions);
  const practice = useRecoilValue(currentPractice);
  const [itemModal, setItemModal] = useState({ open: false, data: null });

  return (
    <Allowed requiredPermissions="patients_medical_history.read">
      <Medications
        itemModal={itemModal}
        setItemModal={setItemModal}
        userPermissions={userPermissions}
        practice={practice}
      />
    </Allowed>
  );
};

const Medications = ({ setItemModal, itemModal, userPermissions, practice }) => {
  const { patient } = useOutletContext();
  const patientId = patient?.id;
  const canCreate = pm(userPermissions, 'patients_medical_history.create');

  const location = useLocation();
  const highlight = location.state?.highlight;
  const [medicationView, setMedicationView] = useState(highlight?.view ?? null);

  useEffect(() => {
    if (highlight?.kind === 'medication') setMedicationView(highlight.view ?? null);
  }, [location.key, highlight]);

  const queryParams = useMemo(
    () => ({ patientId, ...(medicationView ? { view: medicationView } : {}) }),
    [patientId, medicationView]
  );

  const columns = useMemo(
    () =>
      medicationsCols({
        setShowMedicationModal: setItemModal,
        userPermissions,
        practice,
        showCheckboxes: false
      }),
    [setItemModal, userPermissions, practice]
  );

  const formatMedicationsData = useCallback(
    (response) =>
      formatMedicationData({
        medicationData: response?.medicationHistory?.response?.medications,
        patientId
      }),
    [patientId]
  );

  return (
    <>
      <AgTablePage
        quickFilters={[]}
        defaultColDef={SERVER_SORTED_COL_DEF}
        alwaysShowStatusBar
        compactHeader
        title="Medications"
        pageTitle="Medications"
        alwaysShowFilters
        showExport={false}
        noNewButton={!canCreate}
        onNewClick={() => setItemModal({ open: true, data: null })}
        newButtonText="New medication"
        newButtonProps={PRIMARY_NEW_BUTTON}
        noDisplayButton
        patientId={patientId}
        name="patient-history-medications"
        queryKey="direct.format"
        defaultFilters={DEFAULT_FILTERS}
        {...MEDICATION_PRODUCT_NDC_GROUPING_PROPS}
        defaultColumns={columns}
        useData={usePagedMedication}
        queryParams={queryParams}
        queryOptions={{ enabled: !!patientId }}
        dependencies={[patientId, medicationView]}
        formatData={formatMedicationsData}
        headerActions={({ data }) => <MedicationNdcGrouping rawQueryData={data} />}
        headerViews={(viewProps) => (
          <ViewSwitch {...viewProps} activeView={medicationView} onChange={setMedicationView} />
        )}
      />
      <UpsertProviderMedication showModal={itemModal} hideModal={() => setItemModal(null)} />
    </>
  );
};

export default PatientHistoryMedications;
